import React, { useRef, useState, useEffect } from 'react';

interface QuickTrimModalProps {
  open: boolean;
  videoFile: File;
  onClose: () => void;
  onSave: (blob: Blob) => void;
  maxDurationSeconds?: number;
}

const QuickTrimModal: React.FC<QuickTrimModalProps> = ({ open, videoFile, onClose, onSave, maxDurationSeconds = 120 }) => {
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [duration, setDuration] = useState(0);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (open && videoFile) {
      const url = URL.createObjectURL(videoFile);
      setVideoUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [open, videoFile]);

  const onLoadedMeta = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const d = (e.currentTarget as HTMLVideoElement).duration || 0;
    setDuration(d);
    setEnd(Math.min(d, maxDurationSeconds));
  };

  const handleExport = async () => {
    setError(null);
    setExporting(true);
    try {
      const el = videoRef.current!;
      // Mute element to allow autoplay during offscreen capture
      try { el.muted = true; } catch {}
      el.currentTime = start;
      await new Promise<void>((resolve) => {
        const onSeeked = () => { el.removeEventListener('seeked', onSeeked); resolve(); };
        el.addEventListener('seeked', onSeeked);
      });

      const capture = (el as any).captureStream?.() || (el as any).mozCaptureStream?.();
      if (!capture) throw new Error('captureStream not supported');
      const stream = new MediaStream();
      const vTrack = capture.getVideoTracks?.()[0];
      if (vTrack) stream.addTrack(vTrack);

      const chunks: Blob[] = [];
      // Use vp8 for broader compatibility across devices
      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ? 'video/webm;codecs=vp8,opus' : 'video/webm';
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunks.push(ev.data); };
      const stopP = new Promise<void>((res) => { mr.onstop = () => res(); });
      // Give the element a moment to actually render frames before starting
      await el.play().catch(() => {});
      await new Promise((r) => setTimeout(r, 100));
      mr.start(250);
      const ms = Math.min((end - start), maxDurationSeconds) * 1000;
      await new Promise((r) => setTimeout(r, ms));
      mr.stop();
      el.pause();
      await stopP;
      const outType = chunks[0]?.type || mime;
      const out = new Blob(chunks, { type: outType });
      onSave(out);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={{ margin: 0 }}>Quick Trim</h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={styles.content}>
          {videoUrl ? (
            <video 
              ref={videoRef} 
              src={videoUrl} 
              style={{ 
                width: '100%', 
                maxHeight: '400px', 
                background: '#000', 
                borderRadius: 8,
                display: 'block'
              }} 
              controls 
              onLoadedMetadata={onLoadedMeta} 
            />
          ) : (
            <div style={{ width: '100%', maxHeight: '400px', background: '#000', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', padding: 40 }}>
              Loading video...
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label>Start: {start.toFixed(1)}s</label>
            <input type="range" min={0} max={duration || 0} step={0.1} value={start}
              onChange={(e) => {
                const next = Number(e.target.value);
                // Ensure start doesn't exceed end
                const newStart = Math.min(next, end);
                setStart(newStart);
                // Auto-adjust end if the duration would exceed max
                if (end - newStart > maxDurationSeconds) {
                  setEnd(newStart + maxDurationSeconds);
                }
              }} />
            <label>End: {end.toFixed(1)}s</label>
            <input type="range" min={0} max={duration || 0} step={0.1} value={end}
              onChange={(e) => {
                const next = Number(e.target.value);
                // Ensure end is at least start
                const clamped = Math.max(next, start);
                // Enforce max duration constraint
                const enforced = Math.min(clamped, start + maxDurationSeconds, duration);
                setEnd(enforced);
              }} />
            {error && <div style={{ color: '#b71c1c' }}>{error}</div>}
            <button style={styles.saveBtn} onClick={handleExport} disabled={exporting}>{exporting ? 'Exporting…' : 'Save Trim'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  modal: { background: '#fff', borderRadius: 12, width: '95%', maxWidth: 800, overflow: 'visible', maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottom: '1px solid #eee' },
  closeBtn: { background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer' },
  content: { display: 'flex', flexDirection: 'column', gap: 16, padding: 16, overflowY: 'auto' },
  saveBtn: { alignSelf: 'flex-start', padding: '10px 20px', backgroundColor: '#0B6B6F', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' },
};

export default QuickTrimModal;
